import { List, SlidersHorizontal, SquareStack } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CompactSelection, type GridSelection } from '@glideapps/glide-data-grid';
import { AffinityCheckButton, useCandidateCheck, type CheckSelection } from './AffinityCheck.js';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CandidateDeck } from './CandidateDeck.js';
import {
  CandidateFilterDialog,
  candidateFilterChips,
  clearedCandidateFilters,
  stageQuery,
} from './CandidateFilterDialog.js';
import { CandidateGrid } from './CandidateGrid.js';
import { DensityToggle } from '../grid/DensityToggle.js';
import { useGridPreferences } from '../grid/useGridPreferences.js';
import { ALL_CANDIDATE_TYPES, CANDIDATE_SORT_OPTIONS } from '../dealroom/format.js';
import type { Candidate } from '../dealroom/types.js';
import { useCandidateFilters } from '../dealroom/useCandidateFilters.js';
import { useTRPC } from '../trpc.js';
import { ActiveFilters } from '../ui/FilterDialog.js';
import { formatCount } from '../ui/format.js';
import { SegmentedControl, type Segment } from '../ui/SegmentedControl.js';
import { Select } from '../ui/Select.js';
import { useToast } from '../ui/Toast.js';
import { invalidateFundRows } from '../grid/invalidateFundRows.js';

/** How long a person has to stop typing before the search asks the api. */
const SEARCH_DEBOUNCE_MS = 300;

type Decision = 'accept' | 'ignore' | 'skip';

interface Decided {
  id: string;
  name: string;
  decision: Decision;
}

/**
 * Names the feed keeps seeing that are not tracked. Production carries
 * 5,439 of these because every investor in every European round becomes one,
 * most seen once. The Filters dialog (the funds grid's own, with the
 * candidates' groups) cuts that down to the handful worth a look, by what
 * their rounds say as much as by who they are; "show every candidate" is one
 * click away rather than the default, because the default is the view Project
 * A actually wants.
 * Accepting one creates the fund with the identity Dealroom already gave it
 * and moves its rounds over; ignoring one keeps it out of the way without
 * forgetting it.
 *
 * Two ways to work through them. The list reads the shape of the backlog; the
 * deck hands over one at a time, and is how a few hundred get decided in a
 * sitting.
 */
type WorkMode = 'list' | 'decide';

/**
 * Below this a row of ten columns is ten columns nobody can read, so the
 * backlog opens on the cards instead; the list is still one tap away.
 */
const COMPACT = '(max-width: 640px)';

const PREFERENCES_KEY = 'nemo.grid.discovered.v1';

/** The same backlog, read down a list or decided one card at a time. */
const MODES: readonly Segment<WorkMode>[] = [
  { value: 'list', label: 'List', icon: List },
  { value: 'decide', label: 'Decide', icon: SquareStack },
];

export function DealroomCandidates(): React.JSX.Element {
  const trpc = useTRPC();
  const toast = useToast();
  const queryClient = useQueryClient();
  const check = useCandidateCheck();
  const { filters, setFilters } = useCandidateFilters();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const activeFilters = useMemo(() => candidateFilterChips(filters), [filters]);

  const [mode, setMode] = useState<WorkMode>(() =>
    window.matchMedia(COMPACT).matches ? 'decide' : 'list',
  );
  const preferences = useGridPreferences(PREFERENCES_KEY);
  const [searchInput, setSearchInput] = useState(filters.text);
  // What has been decided or passed over this session. Deciding does not
  // invalidate the list: refetching twenty loaded pages to drop one row would
  // throw away the scroll position, and it would throw away the row undo needs.
  const [decided, setDecided] = useState<ReadonlySet<string>>(() => new Set());
  const [skipped, setSkipped] = useState<ReadonlySet<string>>(() => new Set());
  const [history, setHistory] = useState<readonly Decided[]>([]);

  const setText = useRef((text: string) =>
    setFilters((current) => (current.text === text ? current : { ...current, text })),
  );
  useEffect(() => {
    const timer = window.setTimeout(() => setText.current(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  // The list's question, which "select all matching" hands the check as it is.
  const listQuery = useMemo(
    () => ({
      minRounds: filters.minRounds,
      types: [...filters.types],
      ...(filters.text === '' ? {} : { text: filters.text }),
      ...(filters.hqCountries.length === 0 ? {} : { hqCountries: [...filters.hqCountries] }),
      ...(filters.europeOnly ? { region: 'europe' as const } : {}),
      sort: filters.sort,
      ...(filters.coInvestedOnly ? { coInvestedOnly: true } : {}),
      ...stageQuery(filters),
      ...(filters.sectors.length === 0 ? {} : { sectors: [...filters.sectors] }),
      ...(filters.regions.length === 0 ? {} : { regions: [...filters.regions] }),
      ...(filters.knowAnyone.length === 0 ? {} : { knowAnyone: [...filters.knowAnyone] }),
      ...(filters.contact.length === 0 ? {} : { contact: [...filters.contact] }),
      ...(filters.sources.length === 0 ? {} : { sources: [...filters.sources] }),
    }),
    [filters],
  );
  const candidates = useInfiniteQuery(
    trpc.candidates.list.infiniteQueryOptions(listQuery, {
      getNextPageParam: (page) => page.nextCursor,
    }),
  );

  // Ticked rows are kept by id, not by position: the list pages in and drops
  // decided rows, and a position would then tick a different fund.
  const [ticked, setTicked] = useState<ReadonlySet<string>>(() => new Set());
  const [allMatching, setAllMatching] = useState(false);
  const [cursor, setCursor] = useState<GridSelection['current']>(undefined);
  useEffect(() => {
    setTicked(new Set());
    setAllMatching(false);
  }, [listQuery]);
  // The pending backlog's countries, which is what the list above is drawn from.
  const countries = useQuery(trpc.candidates.countries.queryOptions());

  const loaded: readonly Candidate[] = (candidates.data?.pages ?? []).flatMap(
    (page) => page.candidates,
  );
  const rows = loaded.filter((candidate) => !decided.has(candidate.id));
  const deck = rows.filter((candidate) => !skipped.has(candidate.id));
  const first = candidates.data?.pages[0];
  const total = first?.total ?? 0;
  const matching = first?.matching ?? null;

  const forget = useCallback((id: string) => {
    setDecided((current) => {
      const next = new Set(current);
      next.delete(id);
      return next;
    });
  }, []);

  const undo = useMutation(
    trpc.candidates.undo.mutationOptions({
      onSuccess: (_result, variables) => {
        forget(variables.id);
        invalidateFundRows(queryClient, trpc);
        void queryClient.invalidateQueries({ queryKey: trpc.dealroom.status.queryKey() });
      },
      onError: (error) => toast(error.message),
    }),
  );

  const undoOne = useCallback(
    (entry: Decided) => {
      setHistory((current) => current.filter((other) => other.id !== entry.id));
      if (entry.decision === 'skip') {
        setSkipped((current) => {
          const next = new Set(current);
          next.delete(entry.id);
          return next;
        });
        return;
      }
      undo.mutate({ id: entry.id });
    },
    [undo],
  );

  const decide = useMutation(
    trpc.candidates.decide.mutationOptions({
      onSuccess: (result, variables) => {
        const name = loaded.find((row) => row.id === variables.id)?.name ?? 'That name';
        const entry: Decided = { id: variables.id, name, decision: variables.decision };
        if (variables.decision === 'accept') {
          const moved =
            result.participantsRepointed === 1
              ? '1 round now points at it'
              : `${result.participantsRepointed} rounds now point at it`;
          toast(`${name} is tracked — ${moved}.`, {
            tone: 'info',
            action: { label: 'Undo', run: () => undoOne(entry) },
          });
        } else {
          toast(`${name} dismissed.`, {
            tone: 'info',
            action: { label: 'Undo', run: () => undoOne(entry) },
          });
        }
        invalidateFundRows(queryClient, trpc);
        void queryClient.invalidateQueries({ queryKey: trpc.dealroom.status.queryKey() });
      },
      onError: (error, variables) => {
        // The row went the moment the key was pressed; a refusal puts it back.
        forget(variables.id);
        setHistory((current) => current.filter((other) => other.id !== variables.id));
        toast(error.message);
      },
    }),
  );

  const onDecide = useCallback(
    (candidate: Candidate, decision: 'accept' | 'ignore') => {
      if (decided.has(candidate.id)) return;
      setDecided((current) => new Set(current).add(candidate.id));
      setHistory((current) => [...current, { id: candidate.id, name: candidate.name, decision }]);
      decide.mutate({ id: candidate.id, decision });
    },
    [decide, decided],
  );

  const onSkip = useCallback((candidate: Candidate) => {
    setSkipped((current) => new Set(current).add(candidate.id));
    setHistory((current) => [
      ...current,
      { id: candidate.id, name: candidate.name, decision: 'skip' },
    ]);
  }, []);

  const undoLast = useCallback(() => {
    const last = history[history.length - 1];
    if (last === undefined) return;
    undoOne(last);
    toast(`${last.name} is back in Discovered.`, { tone: 'info' });
  }, [history, undoOne, toast]);

  const gridSelection: GridSelection = useMemo(() => {
    let picked = CompactSelection.empty();
    rows.forEach((row, index) => {
      if (allMatching || ticked.has(row.id)) picked = picked.add(index);
    });
    return {
      columns: CompactSelection.empty(),
      rows: picked,
      ...(cursor === undefined ? {} : { current: cursor }),
    };
  }, [rows, ticked, allMatching, cursor]);
  const onSelectionChange = useCallback(
    (next: GridSelection) => {
      setCursor(next.current);
      const ids = next.rows.toArray().flatMap((index) => {
        const row = rows[index];
        return row === undefined ? [] : [row.id];
      });
      setAllMatching(false);
      setTicked(new Set(ids));
    },
    [rows],
  );
  const checkSelection: CheckSelection | null = allMatching
    ? { kind: 'matching', matching: listQuery, count: matching ?? rows.length }
    : ticked.size > 0
      ? { kind: 'ids', candidateIds: [...ticked] }
      : null;

  const fetchMore = useCallback(() => {
    if (candidates.hasNextPage && !candidates.isFetchingNextPage) void candidates.fetchNextPage();
  }, [candidates]);

  if (candidates.isError) {
    return (
      <p className="subtle">Could not load the discovered funds: {candidates.error.message}</p>
    );
  }

  const searching = filters.text !== '';
  const empty = candidates.isPending ? (
    <span className="subtle">Loading the discovered funds…</span>
  ) : (
    <span className="subtle">
      {searching
        ? 'No discovered funds match that search.'
        : total === 0
          ? 'Nothing discovered yet. Names the feed does not recognise show up here.'
          : 'No discovered funds match these filters.'}
    </span>
  );

  return (
    <>
      <div className="toolbar">
        {/* Two numbers, because they answer two questions: how many the
            filters let through, and how big the backlog behind them is. "56
            shown of 56" said neither — it read as the whole list while 4,006
            names sat behind a filter that could not reach them. */}
        <strong className="count">
          {matching === null
            ? `${formatCount(rows.length)} shown`
            : `${formatCount(matching)} match`}
          {' · '}
          {formatCount(total)} pending
        </strong>

        <input
          className="search"
          placeholder="Name or company…"
          aria-label="Search discovered funds"
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
        />

        <button
          className="button"
          type="button"
          aria-haspopup="dialog"
          aria-expanded={filtersOpen}
          onClick={() => setFiltersOpen(true)}
        >
          <SlidersHorizontal aria-hidden="true" />
          Filters
          {activeFilters.length > 0 && <span className="filter-count">{activeFilters.length}</span>}
        </button>
        {/* The noise controls' widest setting, one click away as it always was. */}
        {!(
          filters.minRounds <= 1 &&
          ALL_CANDIDATE_TYPES.every((type) => filters.types.includes(type))
        ) && (
          <button
            className="link-button"
            type="button"
            onClick={() =>
              setFilters((current) => ({ ...current, minRounds: 1, types: ALL_CANDIDATE_TYPES }))
            }
          >
            Show all {formatCount(total)}
          </button>
        )}

        {mode === 'list' && (ticked.size > 0 || allMatching) && (
          <>
            <span className="subtle">
              {allMatching
                ? `All ${formatCount(matching ?? rows.length)} matching selected`
                : `${formatCount(ticked.size)} selected`}
            </span>
            {!allMatching && matching !== null && matching > ticked.size && (
              <button className="link-button" type="button" onClick={() => setAllMatching(true)}>
                Select all {formatCount(matching)} matching
              </button>
            )}
            <button
              className="link-button"
              type="button"
              onClick={() => {
                setTicked(new Set());
                setAllMatching(false);
              }}
            >
              Clear
            </button>
          </>
        )}
        {/* In the deck too, so a check under way keeps saying where it is. */}
        <AffinityCheckButton
          selection={mode === 'list' ? checkSelection : null}
          onStarted={() => {
            setTicked(new Set());
            setAllMatching(false);
          }}
        />

        <span className="spacer" />

        <Select
          className="cand-sort"
          label="Order"
          value={filters.sort}
          options={CANDIDATE_SORT_OPTIONS.map((option) => ({
            value: option.id,
            label: option.label,
          }))}
          onChange={(value) => {
            const chosen = CANDIDATE_SORT_OPTIONS.find((option) => option.id === value);
            if (chosen !== undefined) setFilters((current) => ({ ...current, sort: chosen.id }));
          }}
        />

        {mode === 'list' && (
          <DensityToggle density={preferences.density} onChange={preferences.setDensity} />
        )}
        <SegmentedControl
          label="How to work through them"
          value={mode}
          options={MODES}
          onChange={setMode}
        />
      </div>

      <ActiveFilters
        chips={activeFilters}
        onChange={setFilters}
        onClearAll={() => setFilters(clearedCandidateFilters)}
      />
      {filtersOpen && (
        <CandidateFilterDialog
          filters={filters}
          onChange={setFilters}
          countries={countries.data ?? []}
          matching={matching}
          onClose={() => setFiltersOpen(false)}
        />
      )}

      {mode === 'list' ? (
        <CandidateGrid
          preferences={preferences}
          rows={rows}
          onDecide={onDecide}
          hasMore={candidates.hasNextPage}
          loadingMore={candidates.isFetchingNextPage}
          onReachEnd={fetchMore}
          empty={empty}
          selection={{ value: gridSelection, onChange: onSelectionChange }}
          checking={check.checking}
        />
      ) : (
        <CandidateDeck
          rows={deck}
          onDecide={onDecide}
          onSkip={onSkip}
          onUndo={undoLast}
          canUndo={history.length > 0}
          onNeedMore={fetchMore}
          empty={empty}
        />
      )}
    </>
  );
}
